import { type ComponentProps, type FC, type FocusEvent, type InputHTMLAttributes } from "react";
import { logger } from "@portal/app/_common/lib/logger";
import type { RenderableFormQuestionType } from "@portal/app/_common/types/formFunnel";
import { styled } from "@mui/material/styles";
import { useEmailTypoWarning } from "../hooks/useEmailTypoWarning";
import { EmailTypoWarning } from "../ui-elements/EmailTypoWarning";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import {
  FormInputTextArea,
  FormInputPhoneNumber,
  FormInputDateWithFormik,
  FormInputNumberWithFormik,
} from "../ui-elements/FormInputs";
import { FunnelSelectWithFormik, type FunnelSelectAppearance } from "./FunnelSelect";
import { FormFieldError } from "../ui-elements/Form";
import { ValidatedFormikField } from "../ui-elements/formHelpers";
import { PictureChoiceGroupWithFormik, PictureChoiceItem } from "./PictureChoiceField";
import { MultiSelectGroupWithFormik, MultiSelectItem } from "./MultiSelectField";
import { QuestionDescription } from "./QuestionDescription";
import { useFormFunnelHeadingLevel } from "./FormFunnelHeadingContext";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import { FunnelTextField } from "./FunnelTextField";
import { isFormFunnelZipQuestion } from "@portal/app/_common/lib/formFunnelDynamicOptions";
import {
  HEADING_FONT_STACK,
  SANS_FONT_STACK,
} from "@portal/app/_components/theme/options/typography";

// ---------- Email field with typo detection (isolated so the hook only runs for email fields) ----------

interface EmailFieldWithTypoWarningProps {
  commonProps: Record<string, unknown>;
  autocomplete?: string;
  label?: string;
  helpText?: string;
  fieldId: string;
  description?: QuestionDescriptionProp;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  isPrimaryHeading?: boolean;
  inputMode?: string;
  ariaLabelledBy?: string;
}

const EmailFieldWithTypoWarning: FC<EmailFieldWithTypoWarningProps> = ({
  commonProps,
  autocomplete,
  label,
  helpText,
  fieldId,
  description,
  onBlur,
  isPrimaryHeading = false,
  inputMode,
  ariaLabelledBy,
}) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const {
    onBlur: typoOnBlur,
    isMisspelled,
    warningSx,
  } = useEmailTypoWarning({ fieldName: fieldId });

  // Exclude parent's onBlur from spread — it's composed explicitly below
  const { onBlur: _parentOnBlur, ...restCommonProps } = commonProps;

  const descriptionText = description?.content ? (
    <DescriptionSlot>
      <QuestionDescription
        content={description.content}
        testId={`funnel-field-${fieldId}-description`}
      />
    </DescriptionSlot>
  ) : null;

  return (
    <FunnelFormField data-testid={`funnel-field-${fieldId}`} data-field-key={fieldId}>
      {description?.placement === "above" && descriptionText}
      {label && (
        <FunnelQuestionLabel
          as={isPrimaryHeading ? headingLevel : "label"}
          id={`${fieldId}-label`}
          htmlFor={isPrimaryHeading ? undefined : fieldId}
          data-funnel-question-label
        >
          {label}
        </FunnelQuestionLabel>
      )}
      <FunnelFieldContent>
        <ValidatedFormikField
          as={FunnelTextField}
          type="email"
          fullWidth
          autoComplete={autocomplete || "email"}
          slotProps={{
            htmlInput:
              inputMode || ariaLabelledBy
                ? {
                    ...(inputMode && { inputMode }),
                    ...(ariaLabelledBy && { "aria-labelledby": ariaLabelledBy }),
                  }
                : undefined,
          }}
          {...(inputMode && { inputMode })}
          {...restCommonProps}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            onBlur?.(e);
            typoOnBlur(e);
          }}
          sx={warningSx}
        />
      </FunnelFieldContent>
      {isMisspelled ? <EmailTypoWarning /> : <FormFieldError name={fieldId} />}
      {helpText && <FunnelHelpText>{helpText}</FunnelHelpText>}
      {description?.placement !== "above" && descriptionText}
    </FunnelFormField>
  );
};

type FunnelFormattedTextFieldProps = InputHTMLAttributes<HTMLInputElement> & {
  "data-hj-allow"?: boolean;
};

const FunnelFormattedTextField: FC<FunnelFormattedTextFieldProps> = ({
  inputMode,
  maxLength,
  "aria-labelledby": ariaLabelledBy,
  "data-hj-allow": dataHjAllow,
  ...props
}) => (
  <FunnelTextField
    fullWidth
    {...(props as ComponentProps<typeof FunnelTextField>)}
    slotProps={{
      htmlInput: {
        inputMode,
        maxLength,
        ...(ariaLabelledBy && { "aria-labelledby": ariaLabelledBy }),
        ...(dataHjAllow !== undefined && { "data-hj-allow": dataHjAllow }),
      },
    }}
  />
);

const FunnelPhoneInput: FC<ComponentProps<typeof FormInputPhoneNumber>> = (props) => (
  <FormInputPhoneNumber as={FunnelFormattedTextField} {...props} />
);

/** Question types this renderer handles; the CMS rejects any other type on save. */
export type FieldType = RenderableFormQuestionType;

export interface QuestionOption {
  label: string;
  value: string | number | boolean;
  icon?: string;
}

export interface ConditionalLogic {
  enabled?: boolean;
  dependsOn: string;
  value?: string | number | boolean;
  values?: string[];
  operator: "equals" | "not_equals" | "contains" | "in" | "not_in";
}

export interface QuestionDescriptionProp {
  /** Lexical rich-text JSON from Payload. */
  content?: SerializedEditorState | Record<string, unknown>;
  placement?: "above" | "below";
}

export interface QuestionConfig {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  options?: QuestionOption[];
  required?: boolean;
  helpText?: string;
  disabled?: boolean;
  conditionalLogic?: ConditionalLogic;
  filterMapping?: {
    filterKey?: string;
    valueMapping?: Record<string, string>;
    staticOptions?: boolean | null;
  };
  customerFieldMapping?: {
    enabled?: boolean;
    customerField?: string;
  };
  autocomplete?: string;
  inputMode?: string;
  /** Questions sharing the same pageGroup value are displayed together on one step. */
  pageGroup?: string;
  validation?: {
    pattern?: string;
    customError?: string;
    minLength?: number;
    maxLength?: number;
  };
  /** Optional caption-style helper text rendered above or below the question. */
  description?: QuestionDescriptionProp;
}

interface FormFieldRendererProps {
  question: QuestionConfig;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  onChange?: (value: unknown) => void;
  onReselect?: (value: unknown) => void;
  selectAppearance?: FunnelSelectAppearance;
  /** Render this question prompt as the active screen's semantic heading. */
  isPrimaryHeading?: boolean;
}

export const FormFieldRenderer: FC<FormFieldRendererProps> = ({
  question,
  onBlur,
  onChange,
  onReselect,
  selectAppearance,
  isPrimaryHeading: forcePrimaryHeading = false,
}) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const {
    id,
    type,
    label,
    placeholder,
    options,
    helpText,
    disabled,
    autocomplete,
    inputMode,
    description,
  } = question;
  const labelId = `${id}-label`;
  const normalizedInputMode = inputMode as
    | InputHTMLAttributes<HTMLInputElement>["inputMode"]
    | undefined;
  const isHostedField = selectAppearance === "hosted";
  const isPrimaryHeading = forcePrimaryHeading || isHostedField;
  const isChoiceGroup = type === "radio" || type === "pictureChoice" || type === "multiselect";
  const hasOptionIcons = options?.some((option) => Boolean(option.icon)) ?? false;
  const isZipQuestion =
    isFormFunnelZipQuestion(question) ||
    (question.customerFieldMapping?.enabled &&
      question.customerFieldMapping.customerField === "address.zip");
  const htmlInputProps =
    normalizedInputMode || isPrimaryHeading
      ? {
          ...(normalizedInputMode && { inputMode: normalizedInputMode }),
          ...(isPrimaryHeading && { "aria-labelledby": labelId }),
        }
      : undefined;
  const primaryHeadingInputLabelProps = isPrimaryHeading ? { "aria-labelledby": labelId } : {};
  const zipAdornment = isZipQuestion ? (
    <InputAdornment position="start">
      <FontAwesomeIcon
        icon={faLocationDot}
        color="#c8c5bc"
        aria-hidden="true"
        data-testid="funnel-zip-location-icon"
      />
    </InputAdornment>
  ) : undefined;

  const commonProps = {
    id,
    name: id,
    placeholder,
    disabled,
    onBlur,
    ...(autocomplete && { autoComplete: autocomplete }),
  };

  // Email fields are rendered by a dedicated component so that useEmailTypoWarning
  // is only invoked when the field is actually an email input.
  if (type === "email") {
    return (
      <EmailFieldWithTypoWarning
        commonProps={commonProps}
        autocomplete={autocomplete}
        label={label}
        helpText={helpText}
        fieldId={id}
        description={description}
        onBlur={onBlur}
        isPrimaryHeading={isPrimaryHeading}
        inputMode={normalizedInputMode}
        ariaLabelledBy={isPrimaryHeading ? labelId : undefined}
      />
    );
  }

  const renderField = () => {
    switch (type) {
      case "text":
        return (
          <ValidatedFormikField
            as={FunnelTextField}
            fullWidth
            slotProps={{ htmlInput: htmlInputProps }}
            InputProps={zipAdornment ? { startAdornment: zipAdornment } : undefined}
            {...(normalizedInputMode && { inputMode: normalizedInputMode })}
            {...commonProps}
          />
        );
      case "textarea":
        return (
          <ValidatedFormikField
            as={FormInputTextArea}
            {...(normalizedInputMode && { inputMode: normalizedInputMode })}
            {...primaryHeadingInputLabelProps}
            {...commonProps}
          />
        );
      case "phone":
        return (
          <ValidatedFormikField
            as={FunnelPhoneInput}
            {...(normalizedInputMode && { inputMode: normalizedInputMode })}
            {...primaryHeadingInputLabelProps}
            {...commonProps}
          />
        );
      case "number":
        return (
          <FormInputNumberWithFormik
            as={FunnelFormattedTextField}
            {...(normalizedInputMode && { inputMode: normalizedInputMode })}
            {...primaryHeadingInputLabelProps}
            {...commonProps}
          />
        );
      case "date": {
        // FormInputDateWithFormik has its own default placeholder ("MM/DD/YYYY"),
        // so omit placeholder from commonProps to avoid overriding it with undefined.
        const { placeholder: _datePlaceholder, ...dateProps } = commonProps;
        return (
          <FormInputDateWithFormik
            as={FunnelFormattedTextField}
            parseOnChange={isHostedField}
            {...(normalizedInputMode && { inputMode: normalizedInputMode })}
            {...primaryHeadingInputLabelProps}
            {...dateProps}
          />
        );
      }
      case "select":
        return (
          <FunnelSelectWithFormik
            id={id}
            labelId={labelId}
            name={id}
            options={options || []}
            placeholder={placeholder || "Select one..."}
            disabled={disabled}
            onChange={onChange}
            appearance={selectAppearance ?? "questionnaire"}
          />
        );
      case "radio":
      case "pictureChoice":
        return (
          <PictureChoiceGroupWithFormik
            name={id}
            onChange={onChange}
            onReselect={onReselect}
            hasIcons={hasOptionIcons}
          >
            {options?.map((opt) => (
              <PictureChoiceItem
                key={opt.value.toString()}
                value={opt.value}
                icon={opt.icon}
                disabled={disabled}
                data-testid={type === "radio" ? `radio-${opt.value}` : undefined}
              >
                {opt.label}
              </PictureChoiceItem>
            ))}
          </PictureChoiceGroupWithFormik>
        );
      case "multiselect":
        return (
          <MultiSelectGroupWithFormik name={id} onChange={onChange}>
            {options?.map((opt) => (
              <MultiSelectItem key={opt.value.toString()} value={opt.value} disabled={disabled}>
                {opt.label}
              </MultiSelectItem>
            ))}
          </MultiSelectGroupWithFormik>
        );
      default:
        logger.warn(`[FormFieldRenderer] Unknown field type: ${type}`);
        return null;
    }
  };

  const descriptionText = description?.content ? (
    <DescriptionSlot>
      <QuestionDescription
        content={description.content}
        testId={`funnel-field-${id}-description`}
      />
    </DescriptionSlot>
  ) : null;

  return (
    <FunnelFormField data-testid={`funnel-field-${id}`} data-field-key={id}>
      {description?.placement === "above" && descriptionText}
      {label && (
        <FunnelQuestionLabel
          as={isPrimaryHeading ? headingLevel : "label"}
          id={labelId}
          htmlFor={isPrimaryHeading || type === "select" || isChoiceGroup ? undefined : id}
          data-funnel-question-label
        >
          {label}
        </FunnelQuestionLabel>
      )}
      <FunnelFieldContent>
        {isChoiceGroup ? (
          <FunnelChoiceGroup aria-labelledby={labelId}>{renderField()}</FunnelChoiceGroup>
        ) : (
          renderField()
        )}
      </FunnelFieldContent>
      <FormFieldError name={id} />
      {helpText && <FunnelHelpText>{helpText}</FunnelHelpText>}
      {description?.placement !== "above" && descriptionText}
    </FunnelFormField>
  );
};

// Form funnel styled field components
const FunnelFormField = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin: 0;
`;

const FunnelQuestionLabel = styled("label")`
  font-family: ${HEADING_FONT_STACK};
  width: 100%;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text.primary};
  text-align: left;
  margin: 0 0 20px;
  line-height: 1.2;
  letter-spacing: -0.01em;
`;

const FunnelFieldContent = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const FunnelChoiceGroup = styled("fieldset")`
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
`;

const FunnelHelpText = styled("div")`
  font-family: ${SANS_FONT_STACK};
  font-size: 14px;
  color: #7c7978;
  text-align: center;
  margin-top: 12px;
`;

const DescriptionSlot = styled(Box)`
  width: 100%;
  margin-top: 8px;

  &:first-of-type {
    margin-top: 0;
    margin-bottom: 16px;
  }
`;
